<template>
  <div class="planes-gestion">
    <div class="pagina-cabecera">
      <div>
        <h1 class="pagina-titulo">Planes de Suscripcion</h1>
        <p class="pagina-subtitulo">Define los planes, precios y módulos incluidos en cada plan.</p>
      </div>
      <button class="btn btn--primario" @click="abrirNuevoPlan">
        <i class="bi bi-plus-lg"></i> Nuevo plan
      </button>
    </div>

    <div v-if="error" class="alerta alerta-error">
      <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
    </div>

    <div v-if="cargando" class="planes-grid">
      <div v-for="n in 4" :key="n" class="plan-card plan-card--skeleton"></div>
    </div>

    <div v-else class="planes-grid">
      <div
        v-for="plan in planes"
        :key="plan.id"
        class="plan-card"
        :class="{ 'plan-card--inactivo': !plan.activo }"
      >
        <div class="plan-card__cabecera">
          <h2 class="plan-card__nombre">{{ plan.nombre }}</h2>
          <div class="plan-card__acciones">
            <button class="btn-icono" title="Editar plan" @click="abrirEditorPlan(plan)">
              <i class="bi bi-pencil"></i>
            </button>
            <button class="btn-icono" title="Editar módulos" @click="abrirEditorModulos(plan)">
              <i class="bi bi-grid-3x3-gap"></i>
            </button>
            <button
              class="btn-icono"
              :title="plan.activo ? 'Desactivar' : 'Activar'"
              :class="plan.activo ? 'btn-icono--danger' : 'btn-icono--success'"
              @click="togglePlan(plan)"
            >
              <i :class="plan.activo ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>
          </div>
        </div>

        <div class="plan-card__estado-fila">
          <span class="estado-badge" :class="plan.activo ? 'estado-badge--activo' : 'estado-badge--inactivo'">
            {{ plan.activo ? 'Activo' : 'Inactivo' }}
          </span>
          <span v-if="plan.acceso_movil" class="estado-badge estado-badge--movil">
            <i class="bi bi-phone"></i> Móvil
          </span>
        </div>

        <p class="plan-card__descripcion">{{ plan.descripcion || 'Sin descripción.' }}</p>

        <div class="plan-card__precio">
          <span class="precio-monto">${{ formatear(plan.costo) }}</span>
          <span class="precio-periodo">/ mes</span>
        </div>

        <div class="plan-card__metricas">
          <div class="metrica"><i class="bi bi-building"></i> {{ plan.empresas_activas || 0 }} empresa(s)</div>
          <div class="metrica"><i class="bi bi-grid"></i> {{ plan.total_modulos || 0 }} módulos</div>
        </div>
      </div>
    </div>

    <!-- Modal: Editar / Nuevo plan -->
    <div v-if="modalPlan.visible" class="modal-overlay" @click.self="cerrarModalPlan">
      <div class="modal-contenedor">
        <div class="modal-cabecera">
          <h3>{{ modalPlan.esNuevo ? 'Nuevo Plan' : 'Editar Plan' }}</h3>
          <button class="btn-icono" @click="cerrarModalPlan"><i class="bi bi-x-lg"></i></button>
        </div>
        <form class="modal-cuerpo" @submit.prevent="guardarPlan">
          <div class="campo">
            <label>Nombre *</label>
            <input v-model="modalPlan.form.nombre" type="text" placeholder="Plan Básico" required />
          </div>
          <div class="campo">
            <label>Descripcion</label>
            <textarea v-model="modalPlan.form.descripcion" rows="3"></textarea>
          </div>
          <div class="campo">
            <label>Costo mensual (USD)</label>
            <input v-model.number="modalPlan.form.costo" type="number" step="0.01" min="0" />
          </div>
          <div class="campo-grupo campo-grupo--2col">
            <div class="campo campo--toggle">
              <label><input v-model="modalPlan.form.activo" type="checkbox" /> Plan activo</label>
            </div>
            <div class="campo campo--toggle">
              <label><input v-model="modalPlan.form.acceso_movil" type="checkbox" /> Permite acceso móvil</label>
            </div>
          </div>
          <div class="modal-pie">
            <button type="button" class="btn btn--secundario" @click="cerrarModalPlan">Cancelar</button>
            <button type="submit" class="btn btn--primario" :disabled="modalPlan.guardando">
              <span v-if="modalPlan.guardando"><i class="bi bi-arrow-clockwise spin"></i> Guardando...</span>
              <span v-else>Guardar</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal: Módulos del plan -->
    <div v-if="modalModulos.visible" class="modal-overlay" @click.self="cerrarModalModulos">
      <div class="modal-contenedor modal-contenedor--ancho">
        <div class="modal-cabecera">
          <div>
            <h3>Módulos — {{ modalModulos.planNombre }}</h3>
            <p class="modal-subtitulo">Marca los módulos que incluye este plan.</p>
          </div>
          <button class="btn-icono" @click="cerrarModalModulos"><i class="bi bi-x-lg"></i></button>
        </div>
        <div v-if="modalModulos.cargando" class="modal-cuerpo modal-cuerpo--centrado">
          <i class="bi bi-arrow-clockwise spin grande"></i>
          <span>Cargando módulos...</span>
        </div>
        <div v-else class="modal-cuerpo">
          <div class="modulos-acciones">
            <button class="btn btn--xs btn--secundario" @click="seleccionarTodos(true)"><i class="bi bi-check-all"></i> Todos</button>
            <button class="btn btn--xs btn--secundario" @click="seleccionarTodos(false)"><i class="bi bi-x-square"></i> Ninguno</button>
            <span class="modulos-contador">{{ modalModulos.seleccionados.length }} seleccionados</span>
          </div>
          <div v-for="principal in modalModulos.modulos" :key="principal.id" class="modulo-seccion">
            <div class="modulo-seccion__cabecera">
              <span class="modulo-seccion__nombre">
                <i :class="iconoModulo(principal.ruta_modulo)"></i>
                {{ principal.nombre_modulo }}
              </span>
              <label class="toggle-todo" title="Seleccionar sección">
                <input
                  type="checkbox"
                  :checked="todosSelecionados(principal)"
                  :indeterminate.prop="algunoSeleccionado(principal)"
                  @change="toggleSeccion(principal, $event.target.checked)"
                />
              </label>
            </div>
            <div class="modulo-seccion__items">
              <label v-for="sec in principal.secundarios" :key="sec.id" class="modulo-item" :class="{ 'modulo-item--pendiente': !sec.activo }">
                <input type="checkbox" :value="sec.id" v-model="modalModulos.seleccionados" />
                <span class="modulo-item__nombre">{{ sec.nombre }}</span>
                <span v-if="!sec.activo" class="modulo-item__tag">PENDIENTE</span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn btn--secundario" @click="cerrarModalModulos">Cancelar</button>
          <button type="button" class="btn btn--primario" :disabled="modalModulos.guardando" @click="guardarModulosPlan">
            <span v-if="modalModulos.guardando"><i class="bi bi-arrow-clockwise spin"></i> Guardando...</span>
            <span v-else>Guardar módulos</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

const ICONOS_MODULO = {
  dashboard: 'bi bi-house-door', ventas: 'bi bi-receipt', pedidos: 'bi bi-bag-check',
  proformas: 'bi bi-file-earmark-text', clientes: 'bi bi-people', inventario: 'bi bi-box-seam',
  compras: 'bi bi-bag', caja: 'bi bi-cash-stack', reportes: 'bi bi-bar-chart',
  configuracion: 'bi bi-gear', contabilidad: 'bi bi-calculator',
}

export default {
  name: 'PlanesList',
  data() {
    return {
      planes: [], cargando: true, error: null,
      modalPlan: {
        visible: false, esNuevo: false, guardando: false,
        form: { id: null, nombre: '', descripcion: '', costo: 0, activo: true, acceso_movil: true },
      },
      modalModulos: {
        visible: false, cargando: false, guardando: false,
        planId: null, planNombre: '', modulos: [], seleccionados: [],
      },
    }
  },
  mounted() { this.cargarPlanes() },
  methods: {
    async cargarPlanes() {
      this.cargando = true; this.error = null
      try {
        const res = await api.get('/admin/planes')
        this.planes = res.data?.datos || []
      } catch (e) {
        this.error = 'No se pudieron cargar los planes. ' + (e.response?.data?.mensaje || '')
      } finally { this.cargando = false }
    },
    abrirNuevoPlan() {
      this.modalPlan.esNuevo = true
      this.modalPlan.form = { id: null, nombre: '', descripcion: '', costo: 0, activo: true, acceso_movil: true }
      this.modalPlan.visible = true
    },
    abrirEditorPlan(plan) {
      this.modalPlan.esNuevo = false
      this.modalPlan.form = {
        id: plan.id,
        nombre: plan.nombre,
        descripcion: plan.descripcion || '',
        costo: parseFloat(plan.costo) || 0,
        activo: !!parseInt(plan.activo),
        acceso_movil: !!parseInt(plan.acceso_movil),
      }
      this.modalPlan.visible = true
    },
    cerrarModalPlan() { this.modalPlan.visible = false },
    async guardarPlan() {
      const f = this.modalPlan.form; this.modalPlan.guardando = true
      try {
        const datos = { ...f, activo: f.activo ? 1 : 0, acceso_movil: f.acceso_movil ? 1 : 0 }
        if (this.modalPlan.esNuevo) { await api.post('/admin/planes', datos) }
        else { await api.put(`/admin/planes/${f.id}`, datos) }
        await this.cargarPlanes(); this.cerrarModalPlan()
      } catch (e) {
        alert('Error al guardar: ' + (e.response?.data?.mensaje || e.message))
      } finally { this.modalPlan.guardando = false }
    },
    async togglePlan(plan) {
      if (!confirm(`¿Deseas ${plan.activo ? 'desactivar' : 'activar'} el plan "${plan.nombre}"?`)) return
      try { await api.put(`/admin/planes/${plan.id}/toggle`); await this.cargarPlanes() }
      catch (e) { alert('Error al cambiar el estado del plan.') }
    },
    async abrirEditorModulos(plan) {
      this.modalModulos.planId = plan.id; this.modalModulos.planNombre = plan.nombre
      this.modalModulos.visible = true; this.modalModulos.cargando = true
      this.modalModulos.modulos = []; this.modalModulos.seleccionados = []
      try {
        const res = await api.get(`/admin/planes/${plan.id}/modulos`)
        this.modalModulos.modulos = res.data?.datos || []
        const sel = []
        for (const p of this.modalModulos.modulos)
          for (const s of p.secundarios)
            if (parseInt(s.incluido_en_plan)) sel.push(s.id)
        this.modalModulos.seleccionados = sel
      } catch (e) {
        alert('Error al cargar módulos.'); this.modalModulos.visible = false
      } finally { this.modalModulos.cargando = false }
    },
    cerrarModalModulos() { this.modalModulos.visible = false },
    async guardarModulosPlan() {
      this.modalModulos.guardando = true
      try {
        await api.put(`/admin/planes/${this.modalModulos.planId}/modulos`, { modulos: this.modalModulos.seleccionados })
        await this.cargarPlanes(); this.cerrarModalModulos()
      } catch (e) {
        alert('Error: ' + (e.response?.data?.mensaje || e.message))
      } finally { this.modalModulos.guardando = false }
    },
    seleccionarTodos(estado) {
      if (!estado) { this.modalModulos.seleccionados = []; return }
      const todos = []
      for (const p of this.modalModulos.modulos)
        for (const s of p.secundarios) todos.push(s.id)
      this.modalModulos.seleccionados = todos
    },
    todosSelecionados(principal) {
      return principal.secundarios.every(s => this.modalModulos.seleccionados.includes(s.id))
    },
    algunoSeleccionado(principal) {
      const tiene = principal.secundarios.some(s => this.modalModulos.seleccionados.includes(s.id))
      return tiene && !this.todosSelecionados(principal)
    },
    toggleSeccion(principal, estado) {
      const ids = principal.secundarios.map(s => s.id)
      if (estado) {
        const nuevos = ids.filter(id => !this.modalModulos.seleccionados.includes(id))
        this.modalModulos.seleccionados = [...this.modalModulos.seleccionados, ...nuevos]
      } else {
        this.modalModulos.seleccionados = this.modalModulos.seleccionados.filter(id => !ids.includes(id))
      }
    },
    iconoModulo(codigo) { return ICONOS_MODULO[codigo] || 'bi bi-puzzle' },
    formatear(valor) { return parseFloat(valor || 0).toFixed(2) },
  },
}
</script>

<style scoped>
.planes-gestion { padding:1.5rem; max-width:1400px; margin:0 auto; }
.pagina-cabecera { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:2rem; }
.pagina-titulo   { font-size:1.5rem; font-weight:700; color:var(--text-primary); margin:0 0 .25rem; }
.pagina-subtitulo{ font-size:.875rem; color:var(--text-secondary); margin:0; }
.alerta          { padding:.75rem 1rem; border-radius:.5rem; margin-bottom:1.5rem; display:flex; gap:.5rem; }
.alerta-error    { background:color-mix(in srgb,var(--accent-danger) 10%,transparent); color:var(--accent-danger); border:1px solid color-mix(in srgb,var(--accent-danger) 30%,transparent); }
.planes-grid     { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1.5rem; }
.plan-card       { background:var(--secondary-bg-main); border:1px solid var(--border-main); border-top:4px solid var(--primary-main); border-radius:.75rem; padding:1.25rem; display:flex; flex-direction:column; gap:.75rem; transition:box-shadow .2s; }
.plan-card:hover { box-shadow:var(--shadow-md); }
.plan-card--inactivo { opacity:.55; }
.plan-card--skeleton { min-height:220px; animation:pulso 1.5s ease infinite; background:var(--border-main); }
.plan-card__cabecera  { display:flex; align-items:flex-start; justify-content:space-between; gap:.5rem; }
.plan-card__nombre { font-size:1rem; font-weight:700; color:var(--text-primary); margin:0; }
.plan-card__acciones { display:flex; gap:.25rem; flex-shrink:0; }
.plan-card__estado-fila { display:flex; gap:.5rem; flex-wrap:wrap; }
.plan-card__descripcion { font-size:.8rem; color:var(--text-secondary); margin:0; line-height:1.5; flex:1; }
.plan-card__precio { display:flex; align-items:baseline; gap:.25rem; padding:.75rem 0; border-top:1px solid var(--border-main); border-bottom:1px solid var(--border-main); }
.precio-monto   { font-size:1.4rem; font-weight:700; color:var(--primary-main); }
.precio-periodo { font-size:.75rem; color:var(--text-muted); }
.plan-card__metricas { display:grid; grid-template-columns:1fr 1fr; gap:.4rem; }
.metrica        { display:flex; align-items:center; gap:.4rem; font-size:.78rem; color:var(--text-secondary); }
.estado-badge   { font-size:.7rem; font-weight:600; padding:.15rem .5rem; border-radius:999px; }
.estado-badge--activo  { background:color-mix(in srgb,var(--accent-success) 15%,transparent); color:var(--accent-success); }
.estado-badge--inactivo{ background:color-mix(in srgb,var(--text-muted) 15%,transparent); color:var(--text-muted); }
.estado-badge--movil   { background:color-mix(in srgb,var(--primary-main) 12%,transparent); color:var(--primary-main); }
.btn-icono       { background:none; border:1px solid var(--border-main); border-radius:.375rem; padding:.3rem .5rem; cursor:pointer; color:var(--text-secondary); transition:all .15s; }
.btn-icono:hover { background:var(--bg-main); color:var(--text-primary); }
.btn-icono--danger:hover  { border-color:var(--accent-danger); color:var(--accent-danger); }
.btn-icono--success:hover { border-color:var(--accent-success); color:var(--accent-success); }
.btn         { display:inline-flex; align-items:center; gap:.4rem; border:none; border-radius:.5rem; padding:.6rem 1.2rem; cursor:pointer; font-size:.875rem; font-weight:600; transition:all .15s; }
.btn:disabled { opacity:.5; cursor:not-allowed; }
.btn--primario   { background:var(--primary-main); color:#fff; }
.btn--primario:hover { background:var(--primary-hover); }
.btn--secundario { background:var(--bg-main); color:var(--text-primary); border:1px solid var(--border-main); }
.btn--secundario:hover { background:var(--border-main); }
.btn--xs         { padding:.3rem .75rem; font-size:.8rem; }
.modal-overlay   { position:fixed; inset:0; background:rgba(0,0,0,.5); display:flex; align-items:center; justify-content:center; z-index:1000; padding:1rem; }
.modal-contenedor{ background:var(--secondary-bg-main); border-radius:.75rem; width:100%; max-width:520px; max-height:90vh; display:flex; flex-direction:column; box-shadow:var(--shadow-md); }
.modal-contenedor--ancho { max-width:860px; }
.modal-cabecera  { padding:1.25rem 1.5rem; border-bottom:1px solid var(--border-main); display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; }
.modal-cabecera h3 { margin:0; font-size:1.1rem; font-weight:700; color:var(--text-primary); }
.modal-subtitulo { font-size:.8rem; color:var(--text-secondary); margin:.25rem 0 0; }
.modal-cuerpo    { padding:1.5rem; overflow-y:auto; flex:1; display:flex; flex-direction:column; gap:1rem; }
.modal-cuerpo--centrado { align-items:center; justify-content:center; gap:.75rem; color:var(--text-secondary); }
.modal-pie       { padding:1rem 1.5rem; border-top:1px solid var(--border-main); display:flex; justify-content:flex-end; gap:.75rem; }
.campo-grupo     { display:grid; gap:1rem; }
.campo-grupo--2col { grid-template-columns:1fr 1fr; }
.campo           { display:flex; flex-direction:column; gap:.35rem; }
.campo label     { font-size:.8rem; font-weight:600; color:var(--text-secondary); }
.campo input,.campo textarea { background:var(--bg-main); border:1px solid var(--border-main); border-radius:.375rem; padding:.5rem .75rem; color:var(--text-primary); font-size:.875rem; transition:border-color .15s; width:100%; }
.campo input:focus,.campo textarea:focus { outline:none; border-color:var(--border-focus); }
.campo--toggle label { display:flex; align-items:center; gap:.5rem; cursor:pointer; font-size:.875rem; color:var(--text-primary); }
.modulos-acciones { display:flex; align-items:center; gap:.75rem; padding-bottom:1rem; border-bottom:1px solid var(--border-main); flex-wrap:wrap; }
.modulos-contador { margin-left:auto; font-size:.8rem; color:var(--text-secondary); font-weight:600; }
.modulo-seccion  { border:1px solid var(--border-main); border-radius:.5rem; overflow:hidden; }
.modulo-seccion__cabecera { display:flex; align-items:center; justify-content:space-between; padding:.6rem 1rem; background:var(--bg-main); font-weight:700; font-size:.85rem; color:var(--text-primary); }
.modulo-seccion__nombre { display:flex; align-items:center; gap:.5rem; }
.modulo-seccion__items  { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); }
.modulo-item     { display:flex; align-items:center; gap:.5rem; padding:.5rem 1rem; cursor:pointer; border-top:1px solid var(--border-main); transition:background .1s; font-size:.825rem; }
.modulo-item:hover { background:var(--bg-main); }
.modulo-item input { flex-shrink:0; cursor:pointer; }
.modulo-item__nombre { color:var(--text-primary); flex:1; }
.modulo-item__tag { font-size:.65rem; font-weight:700; color:var(--accent-warning); background:color-mix(in srgb,var(--accent-warning) 12%,transparent); padding:.1rem .4rem; border-radius:.25rem; }
.modulo-item--pendiente { opacity:.7; }
.toggle-todo { cursor:pointer; }
@keyframes pulso { 0%,100%{opacity:1} 50%{opacity:.4} }
.spin { animation:rotar 1s linear infinite; display:inline-block; }
@keyframes rotar { to { transform:rotate(360deg); } }
.grande { font-size:2rem; }
@media (max-width:640px) {
  .campo-grupo--2col { grid-template-columns:1fr; }
  .planes-grid { grid-template-columns:1fr; }
}
</style>
